import { CSSProperties, useEffect, useState } from "react";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import JsonView from "@uiw/react-json-view";
import StreamState, { StreamStateValue } from "./StreamState";

type StreamInfoDialogProps = {
  open: boolean;
  onClose: () => void;
  streamSensor?: string;
  streamId?: string;
  streamState?: StreamStateValue;
  isLoading: boolean;
  errorMessage: string | null;
  detectionStateLabel: string;
  showDetectingSpinner: boolean;
  detectedStreamInfo?: unknown;
  jsonViewTheme: CSSProperties;
};

const StreamInfoDialog = ({
  open,
  onClose,
  streamSensor,
  streamId,
  streamState,
  isLoading,
  errorMessage,
  detectionStateLabel,
  showDetectingSpinner,
  detectedStreamInfo,
  jsonViewTheme
}: StreamInfoDialogProps) => {
  const { authorizedFetch, resolveApiPath } = useAuth();
  type Acceleration = { active: string; requested?: string; decoder?: string; encoder?: string; conversion?: string; memory?: string; fallbackReason?: string; pendingRestart?: boolean; pendingAssignment?: boolean };
  const [acceleration, setAcceleration] = useState<Record<string, Acceleration | null> | null>(null);
  useEffect(() => {
    setAcceleration(null);
    if (!open || !streamId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const update = async () => {
      try {
        const response = await authorizedFetch(resolveApiPath(`/api/streams/${encodeURIComponent(streamId)}/acceleration`), { signal: controller.signal });
        if (response.ok && !controller.signal.aborted) setAcceleration(await response.json());
      } catch { /* A temporary status failure must not interrupt playback. */ }
      if (!controller.signal.aborted) timer = setTimeout(update, 2000);
    };
    void update();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [open, streamId, authorizedFetch, resolveApiPath]);

  const hasStreamInfo = detectedStreamInfo !== undefined && detectedStreamInfo !== null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <TroubleshootIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h6" component="span">
              Stream info
            </Typography>
          </Box>
          {streamSensor ? (
            <Box display="flex" alignItems="center" gap={1}>
              {streamState !== undefined ? <StreamState state={streamState} /> : null}
              <Typography variant="body2" color="text.secondary">
                {streamSensor}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {acceleration && <Stack spacing={1} sx={{ mb: 2 }}>
          {(["playback", "preview"] as const).map((key) => {
            const status = acceleration[key];
            if (!status) return null;
            return <Box key={key}>
              <Typography variant="subtitle2">{key === "playback" ? "Playback" : "Preview"} acceleration: {status.active}</Typography>
              {status.pendingAssignment && <Alert severity="info">Awaiting the next preview assignment to apply {status.requested}.</Alert>}
              {status.pendingRestart && <Alert severity="info">Awaiting stream restart to apply {status.requested}.</Alert>}
              {status.fallbackReason && <Typography variant="body2" color="text.secondary">{status.fallbackReason}</Typography>}
              <Typography variant="caption" color="text.secondary">{[status.decoder, status.conversion, status.encoder].filter(Boolean).join(" / ")}{status.memory ? ` (${status.memory})` : ""}</Typography>
            </Box>;
          })}
          <Typography variant="caption" color="text.secondary">Memory limits measure process RAM; GPU memory is not included.</Typography>
        </Stack>}

        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : (
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Detection state
              </Typography>
              <Chip
                size="small"
                label={detectionStateLabel}
                variant={
                  detectionStateLabel === "Detecting" || detectionStateLabel === "Detected"
                    ? "outlined"
                    : "filled"
                }
                color={
                  detectionStateLabel === "Detecting"
                    ? "warning"
                    : detectionStateLabel === "Detected"
                    ? "success"
                    : "default"
                }
              />
              {showDetectingSpinner ? (
                <CircularProgress size={18} thickness={5} color="warning" sx={{ ml: 0.5 }} />
              ) : null}
            </Box>
            {hasStreamInfo ? (
              <Box
                sx={{
                  maxHeight: { xs: 320, sm: 400 },
                  overflow: "auto",
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  px: 1,
                  py: 1
                }}
              >
                <JsonView
                  value={detectedStreamInfo}
                  style={jsonViewTheme}
                  displayDataTypes={false}
                  highlightUpdates={false}
                />
              </Box>
            ) : (
              <Alert severity="info">No stream info available.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreamInfoDialog;
